import { useNavigate, useLocation } from 'react-router-dom'
import { appRoutes, getActiveTabFromPath, getRouteByKey } from './routes.jsx'

export const useNavigation = () => {
	const navigate = useNavigate()
	const location = useLocation()

	// Get current active tab from location
	const activeTab = getActiveTabFromPath(location.pathname)

	// Navigate to a specific tab with error handling and state persistence
	const navigateToTab = (tabKey) => {
		try {
			const route = getRouteByKey(tabKey)
			if (route && route.path !== '/') {
				// Store navigation state in sessionStorage to prevent white screens
				sessionStorage.setItem('lastActiveTab', tabKey)
				sessionStorage.setItem('lastNavigationTime', Date.now().toString())
				
				// Use navigate with error handling
				navigate(route.path, { replace: false })
			} else {
				// Fallback to dashboard
				sessionStorage.setItem('lastActiveTab', 'dashboard')
				navigate('/dashboard', { replace: false })
			}
		} catch (error) {
			console.error('Navigation error:', error)
			
			// Fallback navigation
			try {
				const route = getRouteByKey(tabKey)
				if (route && route.path !== '/') {
					sessionStorage.setItem('lastActiveTab', tabKey)
					window.location.href = route.path
				} else {
					sessionStorage.setItem('lastActiveTab', 'dashboard')
					window.location.href = '/dashboard'
				}
			} catch (fallbackError) {
				console.error('Fallback navigation failed:', fallbackError)
				// Last resort - reload to dashboard
				sessionStorage.setItem('lastActiveTab', 'dashboard')
				window.location.href = '/dashboard'
			}
		}
	}

	// Navigate to a specific path with error handling
	const navigateToPath = (path) => {
		try {
			navigate(path, { replace: false })
		} catch (error) {
			console.error('Path navigation error:', error)
			// Fallback to window.location
			window.location.href = path
		}
	}

	// Navigate back with error handling
	const goBack = () => {
		try {
			navigate(-1)
		} catch (error) {
			console.error('Go back error:', error)
			// Fallback to window.history
			if (window.history.length > 1) {
				window.history.back()
			} else {
				window.location.href = '/dashboard'
			}
		}
	}

	// Navigate forward with error handling
	const goForward = () => {
		try {
			navigate(1)
		} catch (error) {
			console.error('Go forward error:', error)
			// Fallback to window.history
			window.history.forward()
		}
	}

	// Get current route information
	const getCurrentRoute = () => {
		try {
			return getRouteByKey(activeTab)
		} catch (error) {
			console.error('Error getting current route:', error)
			return null
		}
	}

	// Check if current path matches a specific tab
	const isActiveTab = (tabKey) => {
		try {
			return activeTab === tabKey
		} catch (error) {
			console.error('Error checking active tab:', error)
			return false
		}
	}

	// Get all available routes
	const getAllRoutes = () => {
		try {
			return appRoutes.filter(route => route.path !== '/')
		} catch (error) {
			console.error('Error getting routes:', error)
			return []
		}
	}

	// Force refresh navigation (useful for debugging)
	const forceRefresh = () => {
		try {
			window.location.reload()
		} catch (error) {
			console.error('Force refresh error:', error)
			// Last resort — re-navigate to the current URL. Explicit assign()
			// rather than `href = href`, which is a self-assignment.
			window.location.assign(window.location.href)
		}
	}

	// Get last active tab from session storage
	const getLastActiveTab = () => {
		try {
			const lastTab = sessionStorage.getItem('lastActiveTab')
			const lastTime = sessionStorage.getItem('lastNavigationTime')
			
			// Check if navigation was recent (within last 5 minutes)
			if (lastTime && (Date.now() - parseInt(lastTime)) < 5 * 60 * 1000) {
				return lastTab
			}
		} catch (error) {
			console.warn('Failed to get last active tab:', error)
		}
		return null
	}

	// Check if navigation is stale (component hasn't loaded properly)
	const isNavigationStale = () => {
		try {
			const lastTime = sessionStorage.getItem('lastNavigationTime')
			if (lastTime) {
				// If navigation was more than 10 seconds ago, consider it stale
				return (Date.now() - parseInt(lastTime)) > 10000
			}
		} catch (error) {
			console.warn('Failed to check navigation staleness:', error)
		}
		return false
	}

	return {
		// Navigation functions
		navigateToTab,
		navigateToPath,
		goBack,
		goForward,
		forceRefresh,
		
		// Route information
		activeTab,
		currentPath: location.pathname,
		getCurrentRoute,
		isActiveTab,
		getAllRoutes,
		
		// Router utilities
		navigate,
		location,
		getLastActiveTab,
		isNavigationStale
	}
} 